import { Spin, Tabs } from "antd";
import React, { FC, useEffect, useState, useCallback } from "react";
import { filter, get, map } from "lodash";
import classNames from "classnames";
import { ScrollMenu, VisibilityContext } from "react-horizontal-scrolling-menu";
import { useLocation } from "react-router-dom";
// adding local dependencies
import ActionableInsights from "../actionable-insights";
import ADTFeeds from "../adt-feeds";
import ContactDetails from "../contact-details";
import EncountersData from "../encounters-data";
import HRAQuestionnaire from "../hra-questionnaire";
import MedicalHistory from "../medical-history";
import MyDashboard from "../my-dashboard";
import Overview from "../overview";
import RiskScore from "../risk-score";
import SocialDeterminants from "../social-determinants";
import memberDetailsService from "@services/memberDetails.service";
import { useConfigStore } from "@stores/useConfigStore";
import CareCoordination from "../care-coordination/care-coordination";
import { getClientName } from "@utils/helpers";


import { LeftArrow, RightArrow } from "./arrows";
import "./tabs.scss";
import usePreventBodyScroll from "./usePreventBodyScroll";
import { useHospitalMappingStore } from "@stores/useHospitalMappingStore";
import HealthemSpinner from "@components/healthemSpinner/healthemSpinner";
import EhrData from "../ehr-data";
import CareGap from "../care-gaps";
import Summary from "../summary";
import HealthPlanCareGaps from "../healthplan-caregaps";

type scrollVisibilityApiType = React.ContextType<typeof VisibilityContext>;

/****************************************************************** */

const MemberTabsComponent: FC<any> = ({ callback, loadingForOverview, setMainLoader, overviewChangeHandler, activeTabKey, setActiveTabKey }) => {
  const { TabPane } = Tabs;
  const config: any = useConfigStore((state) => state.config);
  const showHospitalistsModal = useHospitalMappingStore((state) => state.showHospitalistsModal);

  const { disableScroll, enableScroll } = usePreventBodyScroll();
  const clientName = getClientName();

  const tabsConfig: any = {};
  if (config) {
    config.member_detail["view_tabs"].optionValues.forEach((option: any) => {
      tabsConfig[option.name] = option.show;
    });
  }

  const [pinCardIds, setPinCardIds] = useState<any[]>([]);
  const [loader, setLoader] = useState<boolean>(false);
  const [countsMap, setCountsMap] = useState<any>({});
  const [isPinningHappen, isPinningHappenHandler] = useState<boolean | null>(null);
  const location : any = useLocation();
  const onTabChange = useCallback((key: any) => {
    setActiveTabKey(key);
    window.scrollTo(0, 0);
  }, [setActiveTabKey]);
  
  useEffect(() => {
    console.log("MemberTabsComponent.useEffect");
    setLoader(true);
    memberDetailsService.getDataCardIds().subscribe({
      next: (res: any) => {
        setPinCardIds(res?.data?.data.datacardsId);
        setLoader(false);
      },
      error: (err: any) => {
        setLoader(false);
      },
    });

    // Navigating back to ADT from dashBoard Hospital List and CM report
    if(showHospitalistsModal){
      onTabChange("6");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTabChange]);

  useEffect(() => {
    if(location?.state?.tabKeyToFocus){
      setActiveTabKey(location?.state?.tabKeyToFocus);
    }
  },[location?.state?.tabKeyToFocus, setActiveTabKey]);

  const countMapChangeHandler = useCallback( (data: any) => {
    setCountsMap(data);
  },[]);

  let _countsMap = { ...countsMap };
  let cc = [...pinCardIds];
  const pinChangeHandler = (id: any, isAdd: boolean, oKey?: any) => {
    if (isAdd) {
      cc.push(id + "");
      setPinCardIds(cc);
      if (_countsMap?.MyDashboard) Object.assign(_countsMap, { MyDashboard: _countsMap.MyDashboard + 1 });
      else Object.assign(_countsMap, { MyDashboard: 1 });
      isPinningHappenHandler(isPinningHappen === null ? true : !isPinningHappen);
    } else {
      const idx = cc.indexOf(id + "");
      cc.splice(idx, 1);
      setPinCardIds(cc);
      Object.assign(_countsMap, { MyDashboard: _countsMap.MyDashboard - 1 });
      isPinningHappenHandler(isPinningHappen === null ? false : !isPinningHappen);
    }

    if (oKey) {
      if (_countsMap[oKey]) {
        if (isAdd) _countsMap[oKey] = _countsMap[oKey] + 1;
        else _countsMap[oKey] = _countsMap[oKey] - 1;
      } else {
        Object.assign(_countsMap, { [oKey]: 1 });
      }
    }

    // if (oKey === "Overview") {
    //   // let overView = { ...overviewData };
    //   // overviewChangeHandler({});
    //   overviewChangeHandler({ ...overviewData });
    // }
    countMapChangeHandler(_countsMap);
  };

  const tabsOrderConfig: any = {
    default: ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13", "14", "15"],
    Centric: ["1", "14", "2", "6", "8", "9", "13", "15", "7"],
  };
  
  const tabsList = [
    {
      tabName: "My Dashboard",
      count: countsMap.MyDashboard,
      show: tabsConfig["My Dashboard"],
      key:"1"
    },
    {
      tabName: "Profile",
      count: countsMap.Overview,
      show: tabsConfig["Profile"],
      key:"2"
    },
    {
      tabName: "Care Management",
      show: tabsConfig["Care Coordination"],
      key:"3"
    },
    {
      tabName: "ADT",
      show: tabsConfig["ADT Feeds"],
      key:"4"
    },
    {
      tabName: "Risk Score",
      count: countsMap.RiskScore,
      show: tabsConfig["Risk Score"],
      key:"5"
    },
    {
      tabName: "Contact Details",
      count: countsMap.ContactDetails,
      show: tabsConfig["Contact Details"],
      key:"6"
    },
    {
      tabName: clientName === "Centric" ? "Labs Data" : "EHR Data",
      count: countsMap.EHRData,
      show: tabsConfig["EHR Data"],
      key:"7"
    },
    {
      tabName: "Encounter Data",
      show: tabsConfig["Encounters Data"],
      key:"8"
    },
    {
      tabName: "Claims History",
      show: tabsConfig["Medical History"],
      key:"9"
    },
    {
      tabName: "Social Determinants",
      count: countsMap.SocialDeterminants,
      show: tabsConfig["Social Determinants"],
      key:"10"
    },
    {
      tabName: "Actionable Insights",
      count: countsMap.ActionableInsights,
      show: tabsConfig["Actionable Insights"],
      key:"11"
    },
    {
      tabName: "HRA Questionnaire",
      show: tabsConfig["HRA Questionnaire"],
      key:"12"
    },
    {
      tabName: "Care Gaps",
      count: countsMap.CareGaps,
      show: tabsConfig["Care Gaps"],
      key:"13"
    },  
    {
      tabName: "Summary",
      count: countsMap.Summary,
      show: tabsConfig["Summary"],
      key:"14"
    },
    {
      tabName: "HealthPlan Care Gaps",
      count: countsMap.HealthPlanCareGaps,
      show: tabsConfig["HealthPlan Care Gaps"],
      key:"15"
    },
  ];

  const getOrderedTabsList = (tabsList: any, clientType: any) => {
    const tabsOrder = tabsOrderConfig[clientType] || tabsOrderConfig.default;
    return tabsOrder.map((key: any) => tabsList.find((tab: any) => tab.key === key));
  };

  const orderedTabsList = getOrderedTabsList(tabsList, clientName);  

  const visibleTabs = filter(orderedTabsList, (item) => item?.show);

  return !loader ? (
    <>
      <div className="scrolling-tabs">
        <div onMouseEnter={disableScroll} onMouseLeave={enableScroll}>
          <ScrollMenu LeftArrow={LeftArrow} RightArrow={() => RightArrow()} onWheel={onWheel}>
            {map(visibleTabs, (item: any) => {

              const _className =classNames( "scroll-item-title", {
                "scroll-item-title__active": activeTabKey === item.key
            });
            
              return (
                <div
                  key={item.key}
                  className={_className}
                  onClick={() => {
                    onTabChange(`${item.key}`);
                  }}
                >
                  {get(item, 'tabName', "")} { item.count ? `(${item.count})` : ""}
                </div>
              );
            })}
          </ScrollMenu>
        </div>
      </div>

      <Tabs activeKey={activeTabKey} defaultActiveKey="1" onChange={onTabChange} className="tab-menu">
        {tabsConfig["My Dashboard"] && (
          <TabPane tab={`My Dashboard ${countsMap.MyDashboard ? `(${countsMap.MyDashboard})` : ""}`} key="1">
            <MyDashboard
              setMainLoader={setMainLoader}
              pinCardIds={pinCardIds}
              pinChangeHandler={(id: any, isAdd: boolean, oKey: any) => pinChangeHandler(id, isAdd, oKey)}
              countMapChangeHandler={countMapChangeHandler}
              isPinningHappen={isPinningHappen}
              setActiveTabKey={setActiveTabKey}
              activeTabKey={activeTabKey}
            />
          </TabPane>
        )}
        {tabsConfig["Profile"] && (
          <TabPane tab={`Profile ${countsMap.Overview ? `(${countsMap.Overview})` : ""}`} key="2">
             <Overview pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "Overview")} pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Care Coordination"] && (
          <TabPane tab={`Care Management`} key="3">
            <CareCoordination />
          </TabPane>
        )}
        {tabsConfig["ADT Feeds"] && (
          <TabPane tab="ADT" key="4">
            <ADTFeeds pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Risk Score"] && (
          <TabPane tab={`Risk Score ${countsMap.RiskScore ? `(${countsMap.RiskScore})` : ""}`} key="5">
            <RiskScore pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "RiskScore")} pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Contact Details"] && (
          <TabPane tab={`Contact Details ${countsMap.ContactDetails ? `(${countsMap.ContactDetails})` : ""}`} key="6">
            <ContactDetails pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "ContactDetails")} pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["EHR Data"] && (
          <TabPane tab={`EHR Data ${countsMap.EHRData ? `(${countsMap.EHRData})` : ""}`} key="7">
            <EhrData pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "EHRData")} pinCardIds={pinCardIds} />
          </TabPane>
        )}
         {tabsConfig["Encounters Data"] && (
          <TabPane tab="Encounters Data" key="8">
            <EncountersData pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Medical History"] && (
          <TabPane tab="Medical History" key="9">
            <MedicalHistory pinCardIds={pinCardIds}   setActiveTabKey={setActiveTabKey}/>
          </TabPane>
        )}
         {tabsConfig["Social Determinants"] && (
          <TabPane tab={`Social Determinants ${countsMap.SocialDeterminants ? `(${countsMap.SocialDeterminants})` : ""}`} key="10">
            <SocialDeterminants pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "SocialDeterminants")} pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Actionable Insights"] && (
          <TabPane tab={`Actionable Insights ${countsMap.ActionableInsights ? `(${countsMap.ActionableInsights})` : ""}`} key="11">
            <ActionableInsights pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "ActionableInsights")} pinCardIds={pinCardIds} setActiveTabKey={setActiveTabKey} activeTabKey={activeTabKey}/>
          </TabPane>
        )}
        {tabsConfig["HRA Questionnaire"] && (
          <TabPane tab="HRA Questionnaire" key="12">
            <HRAQuestionnaire pinCardIds={pinCardIds} />
          </TabPane>
        )}
        {tabsConfig["Care Gaps"] && (
          <TabPane tab={`Care Gaps ${countsMap.CareGaps ? `(${countsMap.CareGaps})` : ""}`} key="13">
            <CareGap pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "CareGaps")} pinCardIds={pinCardIds}/>
          </TabPane>
        )}
        {tabsConfig["Summary"] && (
          <TabPane tab={`Summary ${countsMap.Summary ? `(${countsMap.Summary})` : ""}`} key="14">
             <Summary />
          </TabPane>
        )}
        {tabsConfig["HealthPlan Care Gaps"] && (
          <TabPane tab={`HealthPlan Care Gaps ${countsMap.HealthPlanCareGaps ? `(${countsMap.HealthPlanCareGaps})` : ""}`} key="15">
            <HealthPlanCareGaps pinChangeHandler={(id: any, isAdd: boolean) => pinChangeHandler(id, isAdd, "HealthPlanCareGaps")} pinCardIds={pinCardIds}/>
          </TabPane>
        )}
      </Tabs>
    </>
  ) : (
    <></>
  );
};

export default MemberTabsComponent;

function onWheel(apiObj: scrollVisibilityApiType, ev: React.WheelEvent): void {
  const isThouchpad = Math.abs(ev.deltaX) !== 0 || Math.abs(ev.deltaY) < 15;

  if (isThouchpad) {
    ev.stopPropagation();
    return;
  }

  if (ev.deltaY < 0) {
    apiObj.scrollNext();
  } else if (ev.deltaY > 0) {
    apiObj.scrollPrev();
  }
}
